/* eslint-disable eqeqeq */
import { SalesTaxOptionType, TicketItemType } from "../../models/enums";
import {
  SettingsPricingTaxRateFormValues,
  SettingsTaxRatesDto,
} from "../../models/pricing";
import { TaxItemDto, TicketItem, TicketSession } from "../../models/ticket";
import TicketSessionStore, { TaxItem } from "../../stores/ticketSessionStore";
import { round2Decimals } from "./functions";
import { v4 as uuid } from "uuid";

export class TaxCalculation {
  public ticketSession: TicketSession;
  public standardTax: SettingsPricingTaxRateFormValues;
  public multipleTaxes: SettingsTaxRatesDto[] | undefined;
  public store: TicketSessionStore;
  public applied: { [key: string]: number };
  public appliedTaxItems: { [key: string]: TaxItem };

  constructor(
    ticketSession: TicketSession,
    store: TicketSessionStore,
    standardTax: SettingsPricingTaxRateFormValues,
    multipleTaxes?: SettingsTaxRatesDto[] | undefined
  ) {
    this.ticketSession = ticketSession;
    this.standardTax = standardTax;
    this.multipleTaxes = multipleTaxes;
    this.store = store;
    this.applied = {};
    this.appliedTaxItems = {};
  }
  //     this.setTaxItem(id, ticketItem, useSpecialFLTax, mtx);

  calculateTax(items: TicketItem[], useTicketItems: boolean, reset: boolean) {
    let totalTax = 0.0;
    this.applied = {};
    this.appliedTaxItems = {};

    const isTaxExempt = this.ticketSession.isTaxExempt;

    if (reset) {
      this.ticketSession.taxRates = [];
    }

    for (let ti of items ?? []) {
      if (!useTicketItems && !this.store.originalTaxRates.has(ti.id) && (ti.taxRates ?? []).length > 0) {
        this.store.originalTaxRates.set(
          ti.id,
          (ti.taxRates ?? []) as SettingsTaxRatesDto[]
        );
      }

      let tiTax = 0.0;

      if (!useTicketItems) {
        this.ticketSession.taxRates = this.multipleTaxes ?? [];
      }

      // Standard tax
      if (!isTaxExempt) {
        const standardTax = this.calculateTicketItemTax(
          ti,
          this.standardTax.saleTaxOption,
          this.standardTax.taxRate,
          this.standardTax.maxTaxAmount,
          "Standard Tax"
        );
        if (reset) {
          ti.taxRate = this.standardTax.taxRate;
        }
        tiTax += standardTax;
      }

      // Multiple taxes
      let rates: SettingsTaxRatesDto[] = [];
      if (useTicketItems) {
        if (this.store.originalTaxRates.has(ti.id)) {
          rates = this.store.originalTaxRates.get(
            ti.id
          ) as SettingsTaxRatesDto[];
        } else if ((ti.taxRates?.length ?? 0) > 0) {
          rates = ti.taxRates ?? [];
        } else {
          rates = this.multipleTaxes ?? [];
        }
      } else {
        rates = this.multipleTaxes ?? [];
      }

      if (reset) {
        ti.taxRates = [];
      }

      for (let rate of rates) {
        if (isTaxExempt && !rate.ignoreTaxExempt) {
          continue;
        }

        if (
          reset &&
          !this.ticketSession.taxRates.find((r) => r.id === rate.id)
        ) {
          this.ticketSession.taxRates.push(rate);
        }

        const additionalTax = this.calculateTicketItemTax(
          ti,
          rate.salesTaxOption,
          rate.taxRate,
          rate.maxTaxAmount,
          rate.name
        );
        if (additionalTax > 0) {
          if (reset) {
            ti.taxRates?.push(rate);
          }
          tiTax += additionalTax;
        }
      }

      ti.tax = tiTax;
      ti.total = round2Decimals(this.getSubTotal(ti) + ti.tax);
      totalTax += tiTax;

      this.store.ticketItems.set(ti.id, ti);
    }

    totalTax = round2Decimals(totalTax);

    this.store.appliedTaxRates = [];
    this.store.appliedTaxItems = [];
    this.ticketSession.taxItems = [];

    for (let key in this.applied) {
      this.store.appliedTaxRates.push({
        name: key,
        taxesApplied: this.applied[key],
      });

      const taxItem: TaxItemDto = {
        id: uuid(),
        taxAmount: this.applied[key],
        taxName: key,
        appliesTo: this.salesTaxOptionAsString(this.appliedTaxItems[key].salesTaxOption),
        salesTaxOptionType: SalesTaxOptionType.Labor,
      }

      this.ticketSession.taxItems.push(taxItem);
      this.store.appliedTaxItems.push(taxItem);
      this.ticketSession.taxRate = this.standardTax.taxRate;
    }

    return totalTax;
  }

  isSpecialFLTax(salesTaxOption: SalesTaxOptionType) {
    return (
      salesTaxOption ===
      (SalesTaxOptionType.Parts |
        SalesTaxOptionType.Labor |
        SalesTaxOptionType.OnTicket)
    );
  }

  calculateTicketItemTax(
    ti: TicketItem,
    salesTaxOption: SalesTaxOptionType,
    taxRate: number,
    maxTaxAmount: number,
    name: string
  ) {
    ti.isTaxable = this.isTaxable(ti, salesTaxOption);

    ti.subTotal = this.getSubTotal(ti);

    if (ti.isTaxable) {
      let tax = round2Decimals(Number(ti.subTotal) * Number(taxRate) * 0.01);
      //console.log(ti, tax, taxRate, maxTaxAmount)
      if (maxTaxAmount > 0) {
        tax = Math.min(maxTaxAmount, tax);
      }
      if (!(name in this.applied)) {
        this.applied[name] = tax;
        this.appliedTaxItems[name] = {
          ticketItem: ti,
          useSpecialFLTax: this.isSpecialFLTax(salesTaxOption),
          taxRate: taxRate,
          taxName: name,
          salesTaxOption: salesTaxOption,
          maxTaxAmount: maxTaxAmount,
        };
      } else {
        this.applied[name] += tax;
        if (maxTaxAmount > 0) {
          this.applied[name] = Math.min(maxTaxAmount, this.applied[name])
        }
      }

      return tax;
    }

    return 0;
  }

  getSubTotal(ti: TicketItem) {
    return (
      ti.subTotal ??
      round2Decimals(
        round2Decimals(ti.rate ? ti.rate : 0.0) * round2Decimals(ti.quantity)
      )
    );
  }

  hasLabor(): boolean {
    return this.ticketSession.ticketItems.some(
      (x) => x.type === TicketItemType.Labor
    );
  }

  hasParts(): boolean {
    return this.ticketSession.ticketItems.some(
      (x) => x.type === TicketItemType.Parts
    );
  }

  isTaxable(ti: TicketItem, salesTaxOption: SalesTaxOptionType): boolean {
    //console.log(ti.type, salesTaxOption)
    switch (ti.type) {
      case TicketItemType.Labor:
        const onTicket =
          (salesTaxOption & SalesTaxOptionType.OnTicket) ===
          SalesTaxOptionType.OnTicket;
        const laborTax =
          (salesTaxOption & SalesTaxOptionType.Labor) ===
          SalesTaxOptionType.Labor;

        return onTicket ? this.hasParts() : laborTax;
      case TicketItemType.Parts:
        return (
          (salesTaxOption & SalesTaxOptionType.Parts) ===
          SalesTaxOptionType.Parts
        );
        case TicketItemType.MiscellaneousCharge:
        case TicketItemType.AdhocMiscellaneous:
          return (
            ti.isTaxable && 
            (salesTaxOption & SalesTaxOptionType.ExemptMisc) === 0    // Misc flag is an exemption
          )
      default:
        return true;
    }
  }

  salesTaxOptionAsString(salesTaxOption: SalesTaxOptionType): string {
    if (salesTaxOption == SalesTaxOptionType.Parts) return "Parts";
    else if (salesTaxOption == SalesTaxOptionType.Labor) return "Labor";
    else if (
      salesTaxOption ==
      (SalesTaxOptionType.Labor | SalesTaxOptionType.Parts)
    )
      return "Parts and Labor";
    else if (
      salesTaxOption ==
      (SalesTaxOptionType.Labor |
        SalesTaxOptionType.Parts |
        SalesTaxOptionType.OnTicket)
    )
      return "Parts and Labor(FL)";
    else return "";
  }
}
